import { useQuery, useQueryClient } from "@tanstack/react-query";
import { socketService } from "@/api";
import { useEffect, useRef, useState } from "react";
import { chatService } from "@/services/ChatService";
import { soundNotificationService } from "@/services/SoundNotificationService";
import { useAuth } from "./auth";
import { useCanAccess } from "@/hooks/useCanAccess";
import { ChatMessage, ChatRoom } from "@/types/chat";

const useChatNotification = () => {
    const queryClient = useQueryClient();
    const canAccess = useCanAccess();
    const { user } = useAuth();

    const [pendingChats, setPendingChats] = useState<ChatRoom[]>([]);
    const [selectedChat, setSelectedChat] = useState<string | null>(null);

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyLastPage, setHistoryLastPage] = useState(1);
    const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
    const [newMessage, setNewMessage] = useState("");
    const [activeChats, setActiveChats] = useState<ChatRoom[]>([]);
    const asChatList = (value: unknown): ChatRoom[] => Array.isArray(value) ? value : [];
    const hasInitializedPendingCount = useRef(false);

    const refreshChatLists = async () => {
        const requests: [Promise<ChatRoom[]>, Promise<ChatRoom[]>] = [
            chatService.getActiveChats(),
            canAccess({ permission: "menu-chat" }) ? chatService.getUnassignedChats() : Promise.resolve([]),
        ];
        const [active, pending] = await Promise.all(requests);
        const latestActiveChats = asChatList(active);
        const latestPendingChats = asChatList(pending);

        if (canAccess({ permission: "menu-chat" })) {
            if (chatService.handlePendingChatUpdate(latestPendingChats)) {
                notify("New chat request", "A new chat is waiting for support.");
            }
            setPendingChats(latestPendingChats);
            queryClient.setQueryData(["pending-chats"], latestPendingChats);
        }
        setActiveChats(latestActiveChats);
        queryClient.setQueryData(["active-chats"], latestActiveChats);
    };

    useQuery({
        queryKey: ["pending-chats"],
        queryFn: () => chatService.getUnassignedChats(),
        enabled: canAccess({ permission: "menu-chat" }),
        onSuccess: (data) => {
            if (!hasInitializedPendingCount.current) {
                // First load: establish the baseline so pre-existing pending
                // chats don't fire a spurious "new chat" notification/sound.
                hasInitializedPendingCount.current = true;
                chatService.initializePendingChatCount(data.length);
            } else if (chatService.handlePendingChatUpdate(data)) {
                notify("New chat request", "A new chat is waiting for support.");
            }
            setPendingChats(asChatList(data));
        },
    });
    useQuery({
        queryKey: ["chat.message", selectedChat],
        queryFn: () => (selectedChat ? chatService.getChatHistory(selectedChat) : null),
        enabled: !!selectedChat,
        onSuccess: (data) => {
            if (data) {
                setMessages(data.messages);
                setHistoryPage(data.pagination?.current_page ?? 1);
                setHistoryLastPage(data.pagination?.last_page ?? 1);
            }
        },
    });

    const loadMoreMessages = async () => {
        if (!selectedChat || isLoadingMoreMessages || historyPage >= historyLastPage) {
            return;
        }

        setIsLoadingMoreMessages(true);
        try {
            const nextPage = historyPage + 1;
            const data = await chatService.getChatHistory(selectedChat, nextPage);
            setMessages((prev) => [...(data.messages ?? []), ...prev]);
            setHistoryPage(data.pagination?.current_page ?? nextPage);
            setHistoryLastPage(data.pagination?.last_page ?? historyLastPage);
        } catch {
            // leave pagination state as-is so scrolling up retries
        } finally {
            setIsLoadingMoreMessages(false);
        }
    };

    useQuery({
        queryKey: ["active-chats"],
        queryFn: () => chatService.getActiveChats(),
        onSuccess: (data) => {
            setActiveChats(asChatList(data));
        },
    });
    const { data: endedChats } = useQuery<ChatRoom[]>({
        queryKey: ["ended-chats"],
        queryFn: () => chatService.getEndedChats(),
        enabled: canAccess({ permission: "ended-chat" }),
    });

    useEffect(() => {
        const initializeAudio = async () => {
            try {
                await soundNotificationService.requestAudioPermission();
            } catch (error) {
                console.warn('Audio permission not granted:', error);
            }
        };

        initializeAudio();

        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);
  const isAway = () => document.hidden || !document.hasFocus();
    const notify = (title: string, body: string, tag?: string) => {
        if ("Notification" in window && Notification.permission === "granted" && isAway()) {
            new Notification(title, { body, icon: "/cac.png", tag });
        }
    };

    useEffect(() => {
        // Setup socket event handlers with sound notifications
        chatService.setupSocketEventHandlers({
            onNewPendingChat: async () => {
                // notify() fires from refreshChatLists()/handlePendingChatUpdate,
                // which is scoped to this staff member's department; this event
                // is broadcast to every connected staff member regardless of department.
                await refreshChatLists();
            },
            onStaffAvailabilityChanged: (data) => {
                console.log('Staff availability changed:', data);
            },
            onStaffStatusUpdate: (data) => {
                console.log('Staff status updated:', data);
            },
            onChatAccepted: (data) => {
                console.log('Chat accepted:', data);
            }
        });

        const handleSocketConnect = async () => {
            await refreshChatLists();
        };

        socketService.socketInstance?.on("connect", handleSocketConnect);

        socketService.socketInstance?.on("chat.message", (data) => {
            const incomingChatId = data?.chat_room?.id;
            const incomingMessage = data?.message;
            const content = incomingMessage?.content;

            if (!content || content.sender_type === "system") return;
            if (String(incomingChatId) === String(selectedChat) && !isAway()) return;

            const sender = content.metadata?.guest_name || incomingMessage?.sender?.name || "Someone";
            const preview = content.message_type === "file" ? `📎 ${content.body}` : content.body || "New message";

            notify(sender, preview, incomingChatId ? String(incomingChatId) : undefined);
        });

        if (selectedChat) {
            socketService.socketInstance?.on("chat.message", (data) => {
                const incomingChatId = data?.chat_room?.id || data?.chatId;
                const incomingMessage = data?.message;

                if (!incomingChatId || String(incomingChatId) !== String(selectedChat)) {
                    return;
                }

                if (incomingMessage?.content && typeof incomingMessage?.id === "number") {
                    setMessages((prev) => {
                        const exists = prev.some((m) => m.id === incomingMessage.id);
                        if (exists) return prev;
                        return [...prev, incomingMessage as ChatMessage];
                    });

                    queryClient.setQueryData(["chat.message", selectedChat], (oldData: any) => {
                        if (!oldData) {
                            return { messages: [incomingMessage] };
                        }

                        const existingMessages = Array.isArray(oldData.messages) ? oldData.messages : [];
                        const exists = existingMessages.some((m: ChatMessage) => m.id === incomingMessage.id);
                        if (exists) return oldData;

                        return {
                            ...oldData,
                            messages: [...existingMessages, incomingMessage],
                        };
                    });
                    return;
                }

                queryClient.invalidateQueries({
                    queryKey: ["chat.message", selectedChat]
                });
            });

            socketService.socketInstance?.on("chat.staff.left", () => {
                queryClient.invalidateQueries({
                    queryKey: ["chat.message", selectedChat]
                });
                queryClient.invalidateQueries({
                    queryKey: ["active-chats"]
                });
            });
        }

        socketService.socketInstance?.on("chat-status", async (data) => {

            if (data?.status === "ended") {
                if (selectedChat === data.chat_room?.id) {
                    queryClient.invalidateQueries({
                        queryKey: ["chat.message", selectedChat]
                    });
                }
                queryClient.invalidateQueries({
                    queryKey: ["active-chats"]
                });
                queryClient.invalidateQueries({
                    queryKey: ["ended-chats"]
                });
            }

            if (data?.status === "active" || data?.status === "accepted") {
                await refreshChatLists();
            }

            if (data?.status === "transferred") {
                const isNewStaff = data.chat_room.assigned_staff?.some((s: { id: number }) => s.id === user?.user_id);
                await refreshChatLists();

                if (isNewStaff) {
                    setSelectedChat(data.chat_room.id);
                    queryClient.invalidateQueries(["chat.message", data.chat_room.id]);
                }
            }

            if (data?.status === "pending" || data?.status === "ended") {
                await refreshChatLists();
            }
        });

        return () => {
            socketService.socketInstance?.off("connect", handleSocketConnect);
            socketService.socketInstance?.off("chat.message");
            socketService.socketInstance?.off("chat.staff.left");
            socketService.socketInstance?.off("chat-status");
            chatService.removeSocketEventHandlers();
        };
    }, [selectedChat, queryClient, user?.user_id]);

    return {
        pendingChats,
        setPendingChats,
        activeChats,
        setActiveChats,
        selectedChat,
        setSelectedChat,
        messages,
        setMessages,
        hasMoreMessages: historyPage < historyLastPage,
        isLoadingMoreMessages,
        loadMoreMessages,
        newMessage,
        setNewMessage,
        endedChats: asChatList(endedChats),
    }
}

export default useChatNotification;
